// src/AuthService.js
import { auth } from './firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  signOut,
} from 'firebase/auth';
import { getDoc, setDoc } from 'firebase/firestore';
import { userDoc } from './utils/firestoreRefs';        // 🔑 UID-based helper

/**
 * Register a new user with email & password, then create Firestore doc keyed by UID.
 */
export async function registerWithEmail(email, password, extraData = {}) {
  // 1) Create user in Firebase Auth
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);

  // 2) Optionally send verification email
  await sendEmailVerification(userCredential.user);

  // 3) Create /users/{uid} doc if brand new
  await createUserDocIfNotExists(userCredential.user, extraData);

  return userCredential;
}

/**
 * Log in an existing user with email & password.
 */
export async function loginWithEmail(email, password) {
  return await signInWithEmailAndPassword(auth, email, password);
}

/**
 * Log out current user
 */
export async function logout() {
  return await signOut(auth);
}

/**
 * Listen for Auth State changes (login/logout)
 */
export function onUserStateChanged(callback) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Create a Firestore doc if it doesn't already exist, keyed by user.uid.
 * extraData might include firstName, phoneNumber, etc.
 */
export async function createUserDocIfNotExists(user, extraData = {}) {
  if (!user) return;

  const uid = user.uid;
  const userRef = userDoc(uid);                         // UID key
  const snap    = await getDoc(userRef);

  if (!snap.exists()) {
    // Merge any extra fields with defaults
    const newUserData = {
      uid,
      email:       user.email || '',
      phoneNumber: user.phoneNumber || extraData.phoneNumber || '',
      firstName:   extraData.firstName || '',
      createdAt:   new Date(),
      role:        'user',
      journeys:    [],
    };

    await setDoc(userRef, newUserData);
    console.log('✅ Created new user doc at UID:', uid);
  } else {
    console.log('User doc already exists for UID:', uid);
  }
}
